/**
 * Create Ritual Page
 * Multi-step form with accordion sections for creating a new ritual
 */

import { zodResolver } from "@hookform/resolvers/zod"
import { Label } from "@radix-ui/react-label"
import { useQuery } from "@tanstack/react-query"
import { MoreVertical, Plus, Trash2 } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useBlocker, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomSelect } from "@/components/forms/customSelect"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { DateTimePicker } from "@/components/forms/dateTimePicker"
import { DurationPicker } from "@/components/forms/durationPicker"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { RadioSelect } from "@/components/forms/radioSelect"
import { TagsInput } from "@/components/forms/tagsInput"
import { MediaUploadSection } from "@/components/media/mediaUploadSection"
import { LeaveConfirmationModal } from "@/components/modals/LeaveConfirmationModal"
import {
  type RitualOrganizer,
  SelectRitualOrganizersModal,
} from "@/components/modals/selectRitualOrganizersModal"
import type { AccordionItem, AccordionRef } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { getCommunityById } from "@/features/communities/services/community.service"
import { markImageUsed } from "@/features/media/services/ai-image.service"
import { ProfileIncompleteModal } from "@/features/profile/components/ProfileIncompleteModal"
import { isProfileFullyCompleted } from "@/features/profile/utils/profileCompletion"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import { getAssetUrl } from "@/utils/assets"
import { type CourseModule, CourseModulesSection } from "../components/CourseModulesSection"
import { CustomRepeatInput } from "../components/CustomRepeatInput"
import { type Organizer as PreviewOrganizer, PreviewRitual } from "../components/PreviewRitual"
import { SuccessModal } from "../components/successModal"
import { useCreateRitual } from "../hooks/useRitualMutations"
import { type RitualFormData, ritualSchema } from "../schemas/ritual.schema"
import type { CreateRitualRequest } from "../services/ritual.service"
import { getRitualDuplicateData } from "../services/ritual.service"
import {
  RITUAL_TYPE_OPTIONS,
  RITUAL_VISIBILITY_OPTIONS,
  RitualTypeEnum,
  type RitualTypeLabel,
  RitualVisibilityEnum,
} from "../types"
import type { Organizer, SectionValidationResult } from "../utils/accordionValidation"
import {
  validateDetailsSection,
  validateInfoSection,
  validateMediaSection,
  validateOrganizersSection,
  validatePrivacySection,
} from "../utils/accordionValidation"
import { getDeselectedValuesForVisibility, getPrivacyConfig } from "../utils/privacyConfig"

interface SelectOption {
  value: string
  label: string
}

export function CreateRitualScreen() {
  const { t } = useTranslation("rituals")
  const [viewMode, setViewMode] = useState<"edit" | "preview">("edit")
  const user = useAuthStore(state => state.user)
  const [searchParams] = useSearchParams()
  const sourceId = searchParams.get("sourceId")
  const communityIdFromUrl = searchParams.get("communityId")

  // Navigation blocker for preview mode - only block internal navigation, not external links
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname && viewMode === "preview",
  )

  // Derived state from blocker
  const showLeaveConfirmationModal = blocker.state === "blocked"

  // Profile completion check
  const [showProfileIncompleteModal, setShowProfileIncompleteModal] = useState(false)
  const isProfileComplete = user ? isProfileFullyCompleted(user) : false

  // Check profile on mount
  useEffect(() => {
    if (user && !isProfileComplete) {
      setShowProfileIncompleteModal(true)
    }
  }, [user, isProfileComplete])

  // Initialize React Hook Form with Zod validation
  const form = useForm<RitualFormData>({
    resolver: zodResolver(ritualSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      title: "",
      type: RitualTypeEnum.IN_PERSON,
      visibility: RitualVisibilityEnum.PUBLIC,
      dateTime: "",
      repeat: "none",
      repeatAt: "",
      repeatAtType: "DAYS",
      duration: "",
      address: "",
      meetingLink: "",
      steps: "",
      participationFee: "Free",
      price: "",
      feeDetails: "",
      minParticipantRequirement: false,
      minParticipants: "",
      registrationDeadline: "",
      description: "",
      whatToBring: "",
      whatYoullExperience: "",
      intention: "",
      websiteOrSocialMedia: "",
      tags: [],
      organizers: [],
      mediaFiles: {
        images: [],
        videos: [],
      },
      privacySettings: {
        visibility: ["everyone"],
        comment: ["owner"],
        edit: ["owner"],
        join: ["everyone"],
      },
    },
  })

  const {
    register,
    control,
    formState: { errors },
    trigger,
    clearErrors,
    setValue,
    getValues,
    watch,
  } = form

  const { data: sourceData, isLoading: isLoadingSource } = useQuery({
    queryKey: ["ritual-duplicate", sourceId],
    queryFn: () => (sourceId ? getRitualDuplicateData(sourceId) : undefined),
    enabled: !!sourceId,
  })

  const { data: communityData } = useQuery({
    queryKey: ["community", communityIdFromUrl],
    queryFn: () => (communityIdFromUrl ? getCommunityById(communityIdFromUrl) : undefined),
    enabled: !!communityIdFromUrl,
  })

  const sourceInitializedRef = useRef(false)
  const communityInitializedRef = useRef(false)

  useEffect(() => {
    if (!sourceData || sourceInitializedRef.current) return
    sourceInitializedRef.current = true

    setValue("title", sourceData.title || "")
    setValue("type", sourceData.type as RitualTypeEnum)
    setValue("dateTime", new Date().toISOString())
    setValue("repeat", sourceData.repeat || "none")
    if (sourceData.repeatAt) setValue("repeatAt", sourceData.repeatAt)
    if (sourceData.repeatAtType) setValue("repeatAtType", sourceData.repeatAtType)
    setValue("duration", sourceData.duration || "")
    setValue("address", sourceData.streetAddress || "")
    setValue("visibility", sourceData.accessType as RitualVisibilityEnum)
    setValue("participationFee", sourceData.participationFee || "Free")
    if (sourceData.price !== null) setValue("price", sourceData.price.toString())
    if (sourceData.feeDetails) setValue("feeDetails", sourceData.feeDetails)
    setValue("description", sourceData.description || "")
    setValue("whatToBring", sourceData.whatToBring || "")
    setValue("whatYoullExperience", sourceData.experienceDetails || "")
    setValue("intention", sourceData.intention || "")
    setValue("steps", sourceData.steps || "")
    setValue("meetingLink", sourceData.meetingLink || "")
    setValue("websiteOrSocialMedia", sourceData.webOrSocialMediaLink || "")
    setValue(
      "tags",
      (sourceData.tags || []).map(t => t.text),
    )
    setTags(sourceData.tags || [])
    setValue("minParticipantRequirement", sourceData.activateMinimumParticipantRequirement ?? false)
    if (sourceData.minParticipants)
      setValue("minParticipants", sourceData.minParticipants.toString())
    if (sourceData.registrationDeadline)
      setValue("registrationDeadline", sourceData.registrationDeadline)

    setPrivacySettings({
      comment: sourceData.whoCanComment || ["everyone"],
      edit: sourceData.whoCanEditRitual || ["organizers"],
      join: sourceData.whoCanJoin || ["everyone"],
      invite: sourceData.whoCanInvite || ["organizers"],
      seeParticipantList: sourceData.whoCanSeeParticipantList || ["everyone"],
    })

    if (sourceData.organizers && sourceData.organizers.length > 0) {
      const currentOrganizers = getValues("organizers")
      const existingIds = new Set(currentOrganizers.map(o => o.id))
      const newOrganizers: Organizer[] = sourceData.organizers
        .filter(o => !existingIds.has(o.id))
        .map(o => ({
          id: o.id,
          type: o.type as "user" | "community",
          name: o.name || "",
          identifier: o.username || null,
          avatar: o.profilePictureUrl || undefined,
          roleId: "host",
          roleName: t("createRitual.roleHost"),
          inviteNote: o.inviteNote || undefined,
        }))
      if (newOrganizers.length > 0) {
        setOrganizers(prev => [...prev, ...newOrganizers])
        setValue("organizers", [...currentOrganizers, ...newOrganizers])
      }
    }

    if (sourceData.videos && sourceData.videos.length > 0) {
      const mapped = sourceData.videos.map(v => ({
        id: crypto.randomUUID(),
        url: v.video_url,
        thumbnail: v.thumbnail_url || "",
        title: v.title || "",
        isProcessed: true,
      }))
      setVideoUrls(mapped)
    }
  }, [sourceData, getValues, setValue, t])

  useEffect(() => {
    if (!sourceData?.media?.length) return

    let coverSet = false
    const newImages = sourceData.media.map(media => {
      const isCover = media.isCover && !coverSet
      if (media.isCover && !coverSet) coverSet = true
      return {
        id: crypto.randomUUID(),
        src: media.url,
        uploadId: media.uploadId,
        isCover,
      }
    })

    if (newImages.length > 0) {
      setMediaFiles(prev => {
        return { ...prev, images: newImages }
      })
    }
  }, [sourceData])

  // Pre-fill course modules
  useEffect(() => {
    if (!sourceData?.courseModules?.length) return

    const mappedModules = sourceData.courseModules.map((m, idx) => ({
      id: crypto.randomUUID(),
      title: m.title || "",
      description: m.description || "",
      duration: m.duration || "",
      order: idx,
      isFree: false,
      uploadId: m.uploadId || undefined,
      video_url: m.video_url || undefined,
      videoPreview: m.video_url || undefined,
      videoFile: undefined,
    }))
    setCourseModules(mappedModules)
  }, [sourceData])

  // Ritual type options - imported from central types
  const ritualTypeOptions = RITUAL_TYPE_OPTIONS

  const repeatOptions: SelectOption[] = [
    { value: "none", label: t("repeat.options.none") },
    { value: "daily", label: t("repeat.options.daily") },
    { value: "weekly", label: t("repeat.options.weekly") },
    { value: "monthly", label: t("repeat.options.monthly") },
    { value: "yearly", label: "Yearly" },
    { value: "custom", label: t("repeat.options.custom") },
  ]

  const participationFeeOptions: SelectOption[] = [
    { value: "Free", label: t("createRitual.options.participationFee.free") },
    { value: "Paid", label: t("createRitual.options.participationFee.paid") },
    { value: "Donation-based", label: t("createRitual.options.participationFee.donation") },
  ]

  // Tags state management
  const [tags, setTags] = useState<Array<{ id?: string; text: string }>>([])

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState(() => {
    const defaultConfig = getPrivacyConfig(RitualVisibilityEnum.PUBLIC)
    return {
      comment: defaultConfig.comment.defaultSelected,
      edit: defaultConfig.edit.defaultSelected,
      join: defaultConfig.join.defaultSelected,
      invite: defaultConfig.invite.defaultSelected,
      seeParticipantList: defaultConfig.seeParticipantList.defaultSelected,
    }
  })

  // Track previous visibility to detect changes
  const prevVisibilityRef = useRef<RitualVisibilityEnum | null>(null)

  // Update privacy settings when visibility changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: watch("visibility") is required for subscription
  useEffect(() => {
    const currentVisibility = watch("visibility") as RitualVisibilityEnum

    if (prevVisibilityRef.current !== currentVisibility) {
      prevVisibilityRef.current = currentVisibility

      const currentPrivacySettings = {
        comment: getValues("privacySettings.comment"),
        edit: getValues("privacySettings.edit"),
        join: getValues("privacySettings.join"),
        invite: getValues("privacySettings.invite"),
        seeParticipantList: getValues("privacySettings.seeParticipantList"),
      }

      const deselected = getDeselectedValuesForVisibility(currentVisibility, currentPrivacySettings)

      if (currentVisibility === RitualVisibilityEnum.PERSONAL) {
        setValue("minParticipantRequirement", false)
        setValue("minParticipants", "")
        setValue("registrationDeadline", "")
        clearErrors("minParticipants")
        clearErrors("registrationDeadline")
      }

      setPrivacySettings({
        comment: deselected.comment,
        edit: deselected.edit,
        join: deselected.join,
        invite: deselected.invite,
        seeParticipantList: deselected.seeParticipantList,
      })

      setValue("privacySettings.comment", deselected.comment)
      setValue("privacySettings.edit", deselected.edit)
      setValue("privacySettings.join", deselected.join)
      setValue("privacySettings.invite", deselected.invite)
      setValue("privacySettings.seeParticipantList", deselected.seeParticipantList)
    }
  }, [watch("visibility"), setValue, getValues])

  // Privacy setting change handlers - use useCallback to prevent infinite loops
  const handleCommentChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, comment: options }))
      setValue("privacySettings.comment", options)
    },
    [setValue],
  )

  const handleEditChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, edit: options }))
      setValue("privacySettings.edit", options)
    },
    [setValue],
  )

  const handleJoinChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, join: options }))
      setValue("privacySettings.join", options)
    },
    [setValue],
  )

  const handleInviteChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, invite: options }))
      setValue("privacySettings.invite", options)
    },
    [setValue],
  )

  const handleSeeParticipantListChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, seeParticipantList: options }))
      setValue("privacySettings.seeParticipantList", options)
    },
    [setValue],
  )

  // Selected organizers for ritual - always include current user as creator
  const [organizers, setOrganizers] = useState<Organizer[]>(() => {
    if (user) {
      // Get cover photo from profile_photos array (same logic as UserDropdown)
      const coverPhoto = user.profile_photos?.find(p => p.is_cover_image || p.isCover)
      return [
        {
          id: user.id,
          type: "user",
          name: user.name || user.username || "You",
          identifier: user.username || null,
          avatar: coverPhoto?.filePath || user.profilePictureUrl || undefined,
          roleId: "host",
          roleName: t("createRitual.roleHost"),
        },
      ]
    }
    return []
  })
  const [isOrganizerModalOpen, setIsOrganizerModalOpen] = useState(false)

  // Auto-add community as organizer when communityId is present in URL
  useEffect(() => {
    if (!communityData || communityInitializedRef.current) return
    communityInitializedRef.current = true

    const existingIds = new Set(organizers.map(o => o.id))
    if (existingIds.has(communityData.id)) return

    const communityAsOrganizer: Organizer = {
      id: communityData.id,
      type: "community",
      name: communityData.name,
      identifier: communityData.handle,
      avatar: communityData.coverImages?.[0] || communityData.avatarUrl || undefined,
      roleId: "host",
      roleName: t("createRitual.roleHost"),
      inviteNote:
        "I would like to submit this ritual to your community for consideration. Kindly review and accept it.",
    }
    const updated = [...organizers, communityAsOrganizer]
    setOrganizers(updated)
    setValue("organizers", updated)
  }, [communityData, organizers, setValue, t])

  // Media state
  const [mediaFiles, setMediaFiles] = useState<{
    images: Array<{ id: string; file?: File; src?: string; isCover: boolean; uploadId?: string }>
    videos: Array<{ id: string; file?: File; src?: string }>
    audios: Array<{
      id: string
      file?: File
      name: string
      sizeBytes: number
      uploadId?: string
      posterFile?: File
      posterUrl?: string
    }>
    aiGenerationId?: string | null
    aiGenerationImageIndex?: number | null
  }>({ images: [], videos: [], audios: [] })

  // Video URLs state (for YouTube/Vimeo embeds)
  const [videoUrls, setVideoUrls] = useState<
    Array<{
      id: string
      url: string
      thumbnail: string
      title?: string
      isEditing?: boolean
      isProcessed?: boolean
      isInvalid?: boolean
    }>
  >([])

  const [mediaError, setMediaError] = useState<string | null>(null)

  // Course modules state (for SELF_GUIDED_COURSE)
  const [courseModules, setCourseModules] = useState<CourseModule[]>([])

  // Preserve conditional field values to prevent data loss when ritual type changes
  // When using register() with conditional rendering, unmounting resets values
  const [preservedValues, setPreservedValues] = useState({
    meetingLink: "",
    steps: "",
  })

  // Organizers validation error
  const [organizersError, setOrganizersError] = useState<string | null>(null)

  // Section validation state
  const [sectionValidation, setSectionValidation] = useState<
    Record<string, SectionValidationResult>
  >({})

  // Module validation errors (for SELF_GUIDED_COURSE)
  const [moduleErrors, setModuleErrors] = useState<
    Record<string, { title?: string; description?: string; video?: string }>
  >({})

  // Course modules section error
  const [courseModulesError, setCourseModulesError] = useState<string | null>(null)

  // Track currently open accordion
  const [openAccordionId, setOpenAccordionId] = useState<string | null>("info")

  // Track which sections have been attempted (opened at least once)
  const [attemptedSections, setAttemptedSections] = useState<Set<string>>(new Set(["info"]))

  // Dynamic sidebar positioning based on actual accordion DOM position
  // Using refs and measurement to calculate real offset for each accordion
  const [sidebarOffset, setSidebarOffset] = useState(0)

  // Refs to track each accordion's DOM element
  const accordionRefs = useRef<Record<string, HTMLElement | null>>({})

  // Reference to the accordion container for offset calculations
  const accordionContainerRef = useRef<HTMLDivElement>(null)

  // Store ref for each accordion item
  const setAccordionRef = (id: string) => (element: HTMLDivElement | null) => {
    if (element) {
      accordionRefs.current[id] = element
    }
  }

  // Calculate the actual offset from container to the open accordion
  const updateSidebarOffset = useCallback(() => {
    if (!openAccordionId || !accordionContainerRef.current) {
      setSidebarOffset(0)
      return
    }

    const containerRect = accordionContainerRef.current.getBoundingClientRect()
    const accordionElement = accordionRefs.current[openAccordionId]

    if (accordionElement) {
      const accordionRect = accordionElement.getBoundingClientRect()
      // Calculate offset relative to the container's top edge
      const offset = accordionRect.top - containerRect.top
      setSidebarOffset(Math.max(0, offset))
    } else {
      setSidebarOffset(0)
    }
  }, [openAccordionId])

  // Update sidebar offset when open accordion changes
  useEffect(() => {
    // Small delay to ensure DOM has updated after accordion animation
    const timer = setTimeout(() => {
      updateSidebarOffset()
    }, 250) // Wait for accordion animation to complete

    return () => clearTimeout(timer)
  }, [updateSidebarOffset])

  // Recalculate on window resize to handle responsive changes
  useEffect(() => {
    const handleResize = () => {
      updateSidebarOffset()
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [updateSidebarOffset])

  // Initial calculation on mount when DOM is ready
  useEffect(() => {
    // Initial calculation after a brief delay to ensure DOM is ready
    const timer = setTimeout(() => {
      updateSidebarOffset()
    }, 100)

    return () => clearTimeout(timer)
  }, [updateSidebarOffset])

  // Handle accordion open change
  const handleAccordionOpenChange = useCallback((openItems: Set<string>) => {
    // Get the first (and only, since allowMultiple=false) open item
    const openItem = Array.from(openItems)[0]
    setOpenAccordionId(openItem || null)

    // Track all opened sections as attempted
    if (openItems.size > 0) {
      setAttemptedSections(prev => new Set([...prev, ...openItems]))
    }
  }, [])

  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Success modal state
  const [createdRitualId, setCreatedRitualId] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  // Mutation hook with automatic cache invalidation and navigation
  const _createRitualMutation = useCreateRitual()

  // Handlers
  const handleAddTag = (tag: string) => {
    const newTags = [...tags, { text: tag }]
    setTags(newTags)
    setValue(
      "tags",
      newTags.map(t => t.text),
    )
    trigger("tags")
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag.text !== tagToRemove)
    setTags(updatedTags)
    setValue(
      "tags",
      updatedTags.map(t => t.text),
    )
    trigger("tags")
  }

  // Handle adding organizers from modal (users and communities)
  const handleAddOrganizers = (newOrganizers: RitualOrganizer[]) => {
    const organizersWithType: Organizer[] = newOrganizers.map(org => ({
      id: org.id,
      type: org.type,
      name: org.name,
      identifier: org.identifier,
      avatar: org.profilePictureUrl || undefined,
      roleId: "host",
      roleName: t("createRitual.roleHost"),
      inviteNote: org.inviteNote,
      dataCount: org.dataCount,
    }))

    const updated = [...organizers, ...organizersWithType]
    setOrganizers(updated)
    setValue("organizers", updated)
    trigger("organizers")
    setOrganizersError(null) // Clear error when organizers are added
    // setIsOrganizerModalOpen(false)
  }

  // Handle removing an organizer
  const handleRemoveOrganizer = (organizerId: string) => {
    // Prevent removing the creator (current user)
    if (organizerId === user?.id) {
      return // Creator cannot be removed
    }
    const updated = organizers.filter(org => org.id !== organizerId)
    setOrganizers(updated)
    setValue("organizers", updated)
    trigger("organizers")
  }

  // Course modules handlers (for SELF_GUIDED_COURSE)
  const handleAddModule = () => {
    const newModule: CourseModule = {
      id: `${Date.now()}-${Math.random()}`,
      title: "",
      description: "",
      duration: "",
      order: courseModules.length,
      isFree: true, // Default to true
    }
    setCourseModules([...courseModules, newModule])
    // Clear section error when a module is added
    setCourseModulesError(null)
  }

  const handleRemoveModule = (moduleId: string) => {
    const updated = courseModules.filter(m => m.id !== moduleId)
    // Reorder remaining modules
    const reordered = updated.map((module, index) => ({ ...module, order: index }))
    setCourseModules(reordered)
    // Clear errors for removed module
    setModuleErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[moduleId]
      return newErrors
    })
  }

  const handleReorderModules = (modules: CourseModule[]) => {
    setCourseModules(modules)
  }

  const handleUpdateModule = (
    moduleId: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => {
    const updated = courseModules.map(module => {
      if (module.id === moduleId) {
        // If fieldOrUpdates is a string, use old behavior (single field update)
        if (typeof fieldOrUpdates === "string") {
          return { ...module, [fieldOrUpdates]: value }
        }
        // Otherwise merge the partial updates
        return { ...module, ...fieldOrUpdates }
      }
      return module
    })
    setCourseModules(updated)

    // Clear errors for this module when it's updated
    setModuleErrors(prev => {
      const newErrors = { ...prev }
      if (newErrors[moduleId]) {
        // Clear specific field errors based on what was updated
        if (typeof fieldOrUpdates === "string") {
          delete newErrors[moduleId][fieldOrUpdates as "title" | "description" | "video"]
          // If no more errors for this module, remove it entirely
          if (Object.keys(newErrors[moduleId]).length === 0) {
            delete newErrors[moduleId]
          }
        }
      }
      return newErrors
    })
  }

  // Watch individual form values to trigger validation on change
  const titleValue = watch("title")
  const typeValue = watch("type")
  const dateTimeValue = watch("dateTime")
  const repeatValue = watch("repeat")
  const repeatAtValue = watch("repeatAt")
  const repeatAtTypeValue = watch("repeatAtType")
  const durationValue = watch("duration")
  const addressValue = watch("address")
  const meetingLinkValue = watch("meetingLink")
  const stepsValue = watch("steps")
  const participationFeeValue = watch("participationFee")
  const priceValue = watch("price")
  const feeDetailsValue = watch("feeDetails")
  const minParticipantRequirementValue = watch("minParticipantRequirement")
  const minParticipantsValue = watch("minParticipants")
  const registrationDeadlineValue = watch("registrationDeadline")

  const descriptionValue = watch("description")
  const whatToBringValue = watch("whatToBring")
  const whatYoullExperienceValue = watch("whatYoullExperience")
  const intentionValue = watch("intention")
  const websiteOrSocialMediaValue = watch("websiteOrSocialMedia")
  const tagsValue = watch("tags")

  const privacyVisibilityValue = watch("privacySettings.visibility")
  const privacyCommentValue = watch("privacySettings.comment")
  const privacyEditValue = watch("privacySettings.edit")
  const privacyJoinValue = watch("privacySettings.join")

  // Validate all accordion sections on form change
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally watch individual values to trigger validation
  useEffect(() => {
    const currentFormValues = watch()
    const isPersonal = currentFormValues.visibility === RitualVisibilityEnum.PERSONAL

    const validation: Record<string, SectionValidationResult> = {
      info: validateInfoSection(currentFormValues),
      details: validateDetailsSection(currentFormValues),
      media: validateMediaSection(mediaFiles),
      organizers: isPersonal ? { isComplete: true } : validateOrganizersSection(organizers),
      privacy: isPersonal ? { isComplete: true } : validatePrivacySection(currentFormValues),
    }

    setSectionValidation(validation)
  }, [
    titleValue,
    typeValue,
    dateTimeValue,
    repeatValue,
    repeatAtValue,
    repeatAtTypeValue,
    durationValue,
    addressValue,
    meetingLinkValue,
    stepsValue,
    participationFeeValue,
    priceValue,
    feeDetailsValue,
    minParticipantRequirementValue,
    minParticipantsValue,
    registrationDeadlineValue,
    descriptionValue,
    whatToBringValue,
    whatYoullExperienceValue,
    intentionValue,
    websiteOrSocialMediaValue,
    tagsValue,
    privacyVisibilityValue,
    privacyCommentValue,
    privacyEditValue,
    privacyJoinValue,
    organizers,
    mediaFiles,
  ])

  // dateTimeRef and handleDateTimeClick removed -- now using DateTimePicker component

  // Build accordion items dynamically based on ritual type
  const getAccordionItems = (): AccordionItem[] => {
    const baseAccordionItems: AccordionItem[] = [
      // INFO ACCORDION
      {
        id: "info",
        title: t("createRitual.accordion.info"),
        userTitle: watch("title"), // Display user's title in collapsed view
        showNextButton: true,
        isComplete: sectionValidation.info?.isComplete,
        hasError: !sectionValidation.info?.isComplete,
        error: sectionValidation.info?.error,
        summary: sectionValidation.info?.summary,
        price: sectionValidation.info?.price,
        content: (
          <div className="space-y-6">
            <p className="text-sm mb-7.5 text-body">{t("createRitual.basicInfoDescription")}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6.25">
              {/* TITLE - Full width */}
              <div className="md:col-span-2">
                <CustomInput
                  {...register("title")}
                  id="title"
                  label={t("createRitual.labels.title")}
                  required
                  placeholder={t("createRitual.placeholders.title")}
                  error={errors.title?.message}
                  onClearError={() => clearErrors("title")}
                  className="pb-2.5 !h-7.5"
                />
              </div>

              {/* VISIBILITY */}
              <div className="md:col-span-2 flex flex-col gap-2.5">
                <Label className="text-xs leading-5 text-body uppercase">
                  {t("createRitual.labels.type")}
                  <span className="ms-1">*</span>
                </Label>
                <div className="flex flex-wrap items-center gap-7.5">
                  {RITUAL_VISIBILITY_OPTIONS.map(option => (
                    <label key={option.value} className="flex items-center gap-2.5 cursor-pointer">
                      <div className="relative flex size-6 items-center justify-center">
                        <input
                          type="radio"
                          name="visibility"
                          value={option.value}
                          checked={watch("visibility") === option.value}
                          onChange={e => {
                            setValue("visibility", e.target.value as RitualVisibilityEnum)
                            clearErrors("visibility")
                          }}
                          className="peer sr-only"
                        />
                        <span className="absolute inset-0 rounded-full border-2 border-brand-olive" />
                        <span className="size-3 rounded-full fill-brand-olive bg-brand-olive text-brand-olive opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold leading-6.25 text-color-brand-offwhite">
                          {option.label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.visibility && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.visibility.message}
                  </p>
                )}
              </div>

              {/* FORMAT - Hide for PERSONAL visibility */}
              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <RadioSelect
                  value={watch("type")}
                  onChange={value => {
                    // Preserve current conditional field values before type change
                    const currentMeetingLink = watch("meetingLink") || ""
                    const currentSteps = watch("steps") || ""

                    setValue("type", value as RitualTypeEnum)
                    clearErrors("type")

                    // Store values in state to persist across type changes
                    setPreservedValues({
                      meetingLink: currentMeetingLink,
                      steps: currentSteps,
                    })
                  }}
                  options={ritualTypeOptions}
                  label={t("createRitual.labels.format")}
                  required
                  error={errors.type?.message}
                  onClearError={() => clearErrors("type")}
                />
              )}

              {/* DATE & TIME - Hidden for SELF_GUIDED and SELF_GUIDED_COURSE */}
              {typeValue !== "SELF_GUIDED" && typeValue !== "SELF_GUIDED_COURSE" && (
                <DateTimePicker
                  value={watch("dateTime")}
                  onChange={value => {
                    setValue("dateTime", value)
                    clearErrors("dateTime")
                  }}
                  label={t("createRitual.labels.dateTime")}
                  required
                  showTime
                  disablePastDates
                  error={errors.dateTime?.message}
                  onClearError={() => clearErrors("dateTime")}
                />
              )}

              {/* REPEAT - Hidden for SELF_GUIDED and SELF_GUIDED_COURSE */}
              {typeValue !== "SELF_GUIDED" && typeValue !== "SELF_GUIDED_COURSE" && (
                <RadioSelect
                  value={watch("repeat")}
                  onChange={value => {
                    setValue("repeat", value)
                    clearErrors("repeat")
                  }}
                  options={repeatOptions}
                  label={t("createRitual.labels.repeat")}
                  required
                  error={errors.repeat?.message}
                  onClearError={() => clearErrors("repeat")}
                />
              )}

              {/* DURATION */}
              <DurationPicker
                value={watch("duration")}
                onChange={value => {
                  setValue("duration", value)
                  clearErrors("duration")
                }}
                label={t("createRitual.labels.duration")}
                required
                error={errors.duration?.message}
                onClearError={() => clearErrors("duration")}
              />

              {/* CUSTOM REPEAT - Shown when repeat is "custom" - Hidden for SELF_GUIDED and SELF_GUIDED_COURSE */}
              {typeValue !== "SELF_GUIDED" &&
                typeValue !== "SELF_GUIDED_COURSE" &&
                watch("repeat") === "custom" && (
                  <div className="md:col-span-2 space-y-2">
                    <CustomRepeatInput
                      control={control}
                      error={errors.repeatAt?.message || errors.repeatAtType?.message}
                      onClearError={() => {
                        clearErrors("repeatAt")
                        clearErrors("repeatAtType")
                      }}
                    />
                    <p className="text-sm text-muted-foreground">{t("repeat.specifyRepeat")}</p>
                  </div>
                )}

              {/* ADDRESS - Only for IN_PERSON and not PERSONAL visibility - Full width */}
              {watch("type") === RitualTypeEnum.IN_PERSON &&
                watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                  <div className="md:col-span-2">
                    <AutocompleteInput
                      label={t("createRitual.labels.address")}
                      value={watch("address") || ""}
                      onLocationSelect={locationData => {
                        setValue("address", locationData.address)
                        setValue("city", locationData.city || "")
                        setValue("state", locationData.state || "")
                        setValue("country", locationData.country || "")
                        setValue("lat", locationData.lat)
                        setValue("long", locationData.lng)
                      }}
                      onInputChange={value => {
                        setValue("address", value)
                      }}
                      placeholder={t("createRitual.placeholders.location")}
                      error={errors.address?.message}
                      onClearError={() => clearErrors("address")}
                    />
                  </div>
                )}

              {/* MEETING LINK - Only for VIRTUAL_COURSE - Full width */}
              {watch("type") === RitualTypeEnum.VIRTUAL_COURSE && (
                <div className="md:col-span-2">
                  <CustomInput
                    {...register("meetingLink", {
                      onChange: e => {
                        // Update preserved value when input changes
                        setPreservedValues(prev => ({ ...prev, meetingLink: e.target.value }))
                      },
                    })}
                    id="meetingLink"
                    label={t("createRitual.labels.meetingLink")}
                    required
                    placeholder="https://meet.google.com/xxx or https://zoom.us/j/xxx"
                    error={errors.meetingLink?.message}
                    onClearError={() => clearErrors("meetingLink")}
                  />
                </div>
              )}

              {/* PARTICIPATION FEE - Hide for PERSONAL visibility */}
              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <>
                  <CustomSelect
                    value={watch("participationFee")}
                    onChange={e => {
                      setValue(
                        "participationFee",
                        e.target.value as "Free" | "Paid" | "Donation-based",
                      )
                      clearErrors("participationFee")
                    }}
                    options={participationFeeOptions}
                    label={t("createRitual.labels.participationFee")}
                    required
                    error={errors.participationFee?.message}
                    onClearError={() => clearErrors("participationFee")}
                  />

                  {/* PRICE - Conditional */}
                  {watch("participationFee") === "Paid" && (
                    <CustomInput
                      {...register("price", {
                        onChange: e => {
                          const value = e.target.value
                          // Prevent negative values
                          if (value && parseFloat(value) < 0) {
                            setValue("price", "0")
                          }
                        },
                      })}
                      id="price"
                      label={t("createRitual.labels.price")}
                      type="number"
                      min="0"
                      step="1"
                      prefix="$"
                      hideSpinButtons
                      required
                      placeholder="10"
                      error={errors.price?.message}
                      onClearError={() => clearErrors("price")}
                    />
                  )}

                  {/* FEE DETAILS - Full width - Conditional */}
                  {watch("participationFee") === "Paid" && (
                    <div className="md:col-span-2">
                      <CustomTextarea
                        {...register("feeDetails")}
                        id="feeDetails"
                        label={t("createRitual.labels.feeDetails")}
                        required
                        placeholder={t("createRitual.placeholders.feeDetails")}
                        error={errors.feeDetails?.message}
                        onClearError={() => clearErrors("feeDetails")}
                      />
                    </div>
                  )}
                </>
              )}

              {/* MINIMUM PARTICIPANT REQUIREMENT - Hidden for PERSONAL visibility */}
              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <>
                  <div className="flex items-center gap-2.5 md:col-span-2">
                    <Checkbox
                      id="minParticipantRequirement"
                      checked={watch("minParticipantRequirement")}
                      onCheckedChange={checked =>
                        setValue("minParticipantRequirement", checked === true)
                      }
                      className="bg-transparent w-6 h-6"
                    />
                    <label
                      htmlFor="minParticipantRequirement"
                      className="text-sm font-semibold leading-6.25 cursor-pointer select-none"
                    >
                      Activate minimum participant requirement
                    </label>
                  </div>

                  {/* MINIMUM NUMBER OF PARTICIPANTS REQUIRED - Conditional */}
                  {watch("minParticipantRequirement") && (
                    <CustomInput
                      {...register("minParticipants")}
                      id="minParticipants"
                      label={t("createRitual.labels.minParticipants")}
                      required
                      type="number"
                      min="1"
                      step="1"
                      placeholder={t("createRitual.placeholders.minParticipants")}
                      error={errors.minParticipants?.message}
                      onClearError={() => clearErrors("minParticipants")}
                      className="pb-2.5 !h-7.5"
                    />
                  )}

                  {/* REGISTRATION DEADLINE - Conditional */}
                  {watch("minParticipantRequirement") && (
                    <DateTimePicker
                      value={watch("registrationDeadline")}
                      onChange={value => {
                        setValue("registrationDeadline", value)
                        clearErrors("registrationDeadline")
                      }}
                      label={t("createRitual.labels.registrationDeadline")}
                      required
                      showTime={false}
                      disablePastDates
                      error={errors.registrationDeadline?.message}
                      onClearError={() => clearErrors("registrationDeadline")}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ),
      },

      // DETAILS ACCORDION
      {
        id: "details",
        title: t("createRitual.accordion.details"),
        showNextButton: true,
        isComplete: sectionValidation.details?.isComplete,
        hasError: !sectionValidation.details?.isComplete,
        error: sectionValidation.details?.error,
        summary: sectionValidation.details?.summary,
        content: (
          <div className="space-y-6.25">
            <p className="text-sm mb-7.5 text-body">{t("createRitual.detailsDescription")}</p>

            {/* DESCRIPTION */}
            <CustomTextarea
              {...register("description")}
              id="description"
              label={t("createRitual.labels.description")}
              required
              rows={-1}
              placeholder={t("createRitual.placeholders.description")}
              error={errors.description?.message}
              onClearError={() => clearErrors("description")}
            />

            {/* WHAT TO BRING - Hide for PERSONAL visibility */}
            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <CustomTextarea
                {...register("whatToBring")}
                id="whatToBring"
                label={t("createRitual.labels.whatToBring")}
                placeholder={t("createRitual.placeholders.whatToBring")}
                error={errors.whatToBring?.message}
                onClearError={() => clearErrors("whatToBring")}
              />
            )}

            {/* WHAT YOU'LL EXPERIENCE - Hide for PERSONAL visibility */}
            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <CustomTextarea
                {...register("whatYoullExperience")}
                id="whatYoullExperience"
                label={t("createRitual.labels.whatYoullExperience")}
                placeholder={t("createRitual.placeholders.whatYouExperience")}
                error={errors.whatYoullExperience?.message}
                onClearError={() => clearErrors("whatYoullExperience")}
              />
            )}

            {/* INTENTION */}
            <CustomTextarea
              {...register("intention")}
              id="intention"
              label={t("createRitual.labels.intention")}
              placeholder={t("createRitual.placeholders.intention")}
              error={errors.intention?.message}
              onClearError={() => clearErrors("intention")}
            />

            {/* STEPS TO PERFORM - Only for SELF_GUIDED */}
            {watch("type") === RitualTypeEnum.SELF_GUIDED && (
              <CustomTextarea
                {...register("steps", {
                  onChange: e => {
                    // Update preserved value when input changes
                    setPreservedValues(prev => ({ ...prev, steps: e.target.value }))
                  },
                })}
                id="steps"
                label={t("createRitual.labels.stepsToPerform")}
                required
                rows={4}
                placeholder={t("createRitual.placeholders.steps")}
                error={errors.steps?.message}
                onClearError={() => clearErrors("steps")}
              />
            )}

            {/* WEBSITE OR SOCIAL MEDIA - Hide for PERSONAL visibility */}
            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <CustomInput
                {...register("websiteOrSocialMedia")}
                id="websiteOrSocialMedia"
                label={t("createRitual.labels.websiteSocial")}
                placeholder="https://your-website.com"
                error={errors.websiteOrSocialMedia?.message}
                onClearError={() => clearErrors("websiteOrSocialMedia")}
              />
            )}

            {/* TAGS */}
            <TagsInput
              id="ritual-tags"
              label={t("createRitual.labels.tags")}
              tags={tags.map(t => t.text)}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              placeholder={t("createRitual.placeholders.tags")}
              error={errors.tags?.message}
              onClearError={() => clearErrors("tags")}
              enableSearch={true}
            />
          </div>
        ),
      },

      // ORGANIZERS ACCORDION
      {
        id: "organizers",
        title: t("createRitual.accordion.organizers"),
        showNextButton: true,
        isComplete: sectionValidation.organizers?.isComplete,
        hasError: !sectionValidation.organizers?.isComplete,
        error: organizersError ?? undefined,
        summary: sectionValidation.organizers?.summary,
        displayName: sectionValidation.organizers?.displayName,
        userPreviews: sectionValidation.organizers?.userPreviews,
        showViewMore: organizers.length > 3,
        content: (
          <div className="space-y-5">
            <p className="text-body">{t("createRitual.organizersDescription")}</p>
            {/* Validation Error */}
            {organizersError && (
              <p className="text-xs text-brand-terracotta-bright">{organizersError}</p>
            )}
            {/* Selected Organizers Display */}
            {organizers.length > 0 && (
              <div className="space-y-5 py-2.5">
                {organizers.map(org => (
                  <div key={org.id} className="flex items-center gap-4">
                    {/* Avatar - 48px with themed background */}
                    <div className="w-12.5 h-12.5 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                      {org.avatar ? (
                        <img
                          src={org.avatar}
                          alt={org.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-semibold">{(org.name?.[0] ?? "").toUpperCase()}</span>
                      )}
                    </div>

                    {/* Organizer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">
                          {org.name || t("createRitual.fallback.noName")}
                        </p>
                      </div>
                      <p className="text-sm truncate">
                        {org.identifier ? `@${org.identifier}` : ""}
                      </p>
                    </div>

                    <span className="flex items-center gap-1.5 text-xs text-white">
                      <img src={getAssetUrl("add_person.svg")} alt="" className="w-3.5 h-3.5" />
                      Invited
                    </span>

                    {/* Action Icons - only show for non-creator organizers */}
                    {org.id !== user?.id ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                            <MoreVertical size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleRemoveOrganizer(org.id)}
                            className="text-red focus:text-red"
                          >
                            <Trash2 size={16} />
                            {t("createRitual.buttons.remove")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      // Spacer for creator to maintain layout
                      <div className="w-8" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Invite Organizer Button */}
            <Button
              variant="ghost"
              onClick={() => setIsOrganizerModalOpen(true)}
              className="flex items-center gap-1.25 px-0 h-fit rounded-lg text-sm font-semibold text-primary"
            >
              <Plus size={20} />
              {t("createRitual.buttons.inviteOrganizer")}
            </Button>
          </div>
        ),
      },

      // MEDIA ACCORDION
      {
        id: "media",
        title: t("createRitual.accordion.uploadMedia"),
        showNextButton: true,
        isComplete: sectionValidation.media?.isComplete,
        hasError: !sectionValidation.media?.isComplete || mediaError !== null,
        error: sectionValidation.media?.error ?? mediaError ?? undefined,
        summary: sectionValidation.media?.summary,
        content: (
          <div className="space-y-4">
            <p className="text-sm text-body mb-5">{t("createRitual.mediaDescription")}</p>
            <MediaUploadSection
              initialImages={mediaFiles.images}
              initialVideos={mediaFiles.videos}
              initialAudios={mediaFiles.audios}
              initialVideoUrls={videoUrls}
              onMediaChange={incoming => {
                setMediaFiles(prev => {
                  const incomingIds = new Set(incoming.images.map((i: { id: string }) => i.id))
                  const existingWithUploadId = prev.images.filter(
                    img => !!img.uploadId && !incomingIds.has(img.id),
                  )
                  return {
                    ...prev,
                    ...incoming,
                    images: [...existingWithUploadId, ...incoming.images],
                    audios: incoming.audios ?? prev.audios,
                  }
                })

                // Clear error if media is valid
                if (
                  incoming.images.length > 0 &&
                  incoming.images.some((img: { isCover: boolean }) => img.isCover)
                ) {
                  setMediaError(null)
                }
              }}
              onVideoUrlsChange={videos => setVideoUrls(videos)}
              showVideoSection={true}
              showVideoUrlInput={true}
              showAudioSection={true}
              maxFiles={4}
              maxFileSize={5 * 1024 * 1024}
              maxAudioFileSize={100 * 1024 * 1024}
              allowedTypes={["image/png", "image/jpeg", "image/jpg", "audio/mpeg", "audio/mp3"]}
              enableAiGeneration
              aiEntityType="ritual"
              aiEntityContext={{
                name: titleValue,
                description: descriptionValue,
                type: typeValue,
              }}
            />
            {mediaError && (
              <p className="text-xs text-brand-terracotta-bright mt-1">{mediaError}</p>
            )}
          </div>
        ),
      },

      // PRIVACY ACCORDION
      {
        id: "privacy",
        title: t("createRitual.accordion.privacy"),
        showNextButton: true,
        isComplete: sectionValidation.privacy?.isComplete,
        hasError: !sectionValidation.privacy?.isComplete,
        error: sectionValidation.privacy?.error,
        summary: sectionValidation.privacy?.summary,
        content: (() => {
          const visibility = watch("visibility") as RitualVisibilityEnum
          const privacyConfig = getPrivacyConfig(visibility)
          return (
            <div className="space-y-5">
              <p className="text-body">{t("createRitual.privacyDescription")}</p>

              {/* WHO CAN JOIN */}
              <MultiSelectDropdown
                label={t("createRitual.privacySettings.whoCanJoin")}
                options={privacyConfig.join.options}
                selectedOptions={privacySettings.join}
                onChange={handleJoinChange}
                disabledOptions={privacyConfig.join.disabledOptions}
                autoSelectedOptions={privacyConfig.join.autoSelected}
                groupExclusion={privacyConfig.join.groupExclusion}
              />

              {/* WHO CAN INVITE */}
              <MultiSelectDropdown
                label={t("createRitual.privacySettings.whoCanInvite")}
                options={privacyConfig.invite.options}
                selectedOptions={privacySettings.invite}
                onChange={handleInviteChange}
                disabledOptions={privacyConfig.invite.disabledOptions}
                autoSelectedOptions={privacyConfig.invite.autoSelected}
              />

              {/* WHO CAN COMMENT */}
              <MultiSelectDropdown
                label={t("createRitual.privacySettings.whoCanComment")}
                options={privacyConfig.comment.options}
                selectedOptions={privacySettings.comment}
                onChange={handleCommentChange}
                disabledOptions={privacyConfig.comment.disabledOptions}
                autoSelectedOptions={privacyConfig.comment.autoSelected}
              />

              {/* WHO CAN EDIT */}
              <MultiSelectDropdown
                label={t("createRitual.privacySettings.whoCanEdit")}
                options={privacyConfig.edit.options}
                selectedOptions={privacySettings.edit}
                onChange={handleEditChange}
                disabledOptions={privacyConfig.edit.disabledOptions}
                autoSelectedOptions={privacyConfig.edit.autoSelected}
              />

              {/* WHO CAN SEE PARTICIPANT LIST */}
              <MultiSelectDropdown
                label={t("createRitual.privacySettings.whoCanSeeParticipantList")}
                options={privacyConfig.seeParticipantList.options}
                selectedOptions={privacySettings.seeParticipantList}
                onChange={handleSeeParticipantListChange}
                disabledOptions={privacyConfig.seeParticipantList.disabledOptions}
                autoSelectedOptions={privacyConfig.seeParticipantList.autoSelected}
                selectAllWith={privacyConfig.seeParticipantList.selectAllWith}
              />
            </div>
          )
        })(),
      },
    ]

    // Filter out Organizers and Privacy accordions for PERSONAL rituals
    const isPersonal = watch("visibility") === RitualVisibilityEnum.PERSONAL
    const items = baseAccordionItems.filter(item => {
      if (isPersonal) {
        return item.id !== "organizers" && item.id !== "privacy"
      }
      return true
    })

    // Add Course Modules accordion for SELF_GUIDED_COURSE (after Media, before Privacy)
    if (watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE) {
      // Find the index of the Media accordion
      const mediaIndex = items.findIndex(item => item.id === "media")
      // Insert Course Modules after Media (index + 1)
      items.splice(mediaIndex + 1, 0, {
        id: "courseModules",
        title: t("createRitual.accordion.courseModules"),
        showNextButton: true,
        isComplete:
          courseModules.length > 0 &&
          courseModules.every(m => m.title && m.description && (m.videoFile || m.uploadId)),
        hasError: courseModulesError !== null || Object.keys(moduleErrors).length > 0,
        error: courseModulesError ?? undefined,
        summary:
          courseModules.length > 0
            ? `${courseModules.length} module${courseModules.length > 1 ? "s" : ""}`
            : undefined,
        content: (
          <div className="space-y-4">
            <p className="text-body">{t("createRitual.courseModulesDescription")}</p>
            <CourseModulesSection
              modules={courseModules}
              onAddModule={handleAddModule}
              onRemoveModule={handleRemoveModule}
              onReorderModules={handleReorderModules}
              onUpdateModule={handleUpdateModule}
              moduleErrors={moduleErrors}
              sectionError={courseModulesError}
            />
          </div>
        ),
      })
    }

    return items
  }

  const accordionRef = useRef<AccordionRef>(null)

  // Get accordion items - recomputed on each render to handle conditional accordions
  const accordionItems = getAccordionItems()

  // Validate course modules - extracted as reusable function
  const validateCourseModules = useCallback(() => {
    // Validate that at least one module exists
    if (courseModules.length === 0) {
      setCourseModulesError(t("createRitual.validation.atLeastOneModule"))
      setModuleErrors({})
      return false
    }

    // Clear section error if modules exist
    setCourseModulesError(null)

    // Validate each module and collect errors
    const errors: Record<string, { title?: string; description?: string; video?: string }> = {}
    let hasErrors = false

    courseModules.forEach(module => {
      const moduleErrors: { title?: string; description?: string; video?: string } = {}

      if (!module.title || module.title.trim() === "") {
        moduleErrors.title = t("createRitual.validation.moduleTitleRequired")
        hasErrors = true
      }

      if (!module.description || module.description.trim() === "") {
        moduleErrors.description = t("createRitual.validation.moduleDescriptionRequired")
        hasErrors = true
      }

      if (!module.videoFile && !module.uploadId) {
        moduleErrors.video = t("createRitual.validation.moduleVideoRequired")
        hasErrors = true
      }

      if (Object.keys(moduleErrors).length > 0) {
        errors[module.id] = moduleErrors
      }
    })

    setModuleErrors(errors)
    return !hasErrors
  }, [courseModules, t])

  // Handle Next button click with validation
  const handleNext = async (currentIndex: number): Promise<boolean> => {
    const currentAccordionId = accordionItems[currentIndex].id

    // Determine which fields to validate based on current accordion
    let fieldsToValidate: (keyof RitualFormData)[] = []

    switch (currentAccordionId) {
      case "info":
        fieldsToValidate = ["title", "type", "dateTime", "repeat", "duration", "participationFee"]
        if (watch("participationFee") === "Paid") {
          fieldsToValidate.push("price", "feeDetails")
        }
        if (watch("type") === RitualTypeEnum.IN_PERSON) {
          fieldsToValidate.push("address")
        }
        if (watch("type") === RitualTypeEnum.VIRTUAL_COURSE) {
          fieldsToValidate.push("meetingLink")
        }
        if (watch("minParticipantRequirement") === true) {
          fieldsToValidate.push("minParticipants", "registrationDeadline")
        }
        break
      case "details":
        fieldsToValidate = ["description", "tags"]
        if (watch("type") === RitualTypeEnum.SELF_GUIDED) {
          fieldsToValidate.push("steps")
        }
        break
      case "courseModules": {
        return validateCourseModules()
      }
      case "media": {
        // Custom validation for images (not in form schema)
        if (!mediaFiles.images || mediaFiles.images.length === 0) {
          setMediaError(t("createRitual.validation.atLeastOneImage"))
          return false
        }

        const hasCover = mediaFiles.images.some(img => img.isCover)
        if (!hasCover) {
          setMediaError(t("createRitual.validation.selectCoverImage"))
          return false
        }

        setMediaError(null) // Clear error if valid
        return true
      }
      case "organizers": {
        // Skip validation for personal rituals
        if (watch("visibility") === RitualVisibilityEnum.PERSONAL) {
          return true
        }
        if (!organizers || organizers.length === 0) {
          setOrganizersError(t("createRitual.validation.atLeastOneOrganizer"))
          return false
        }
        setOrganizersError(null)
        return true
      }
      case "privacy":
        // Skip validation for personal rituals
        if (watch("visibility") === RitualVisibilityEnum.PERSONAL) {
          return true
        }
        fieldsToValidate = ["privacySettings"]
        break
      default:
        return true
    }

    // Trigger validation for specific fields
    const isValid = await trigger(fieldsToValidate)

    return isValid
  }

  // Handle preview ritual
  const handlePreview = () => {
    setViewMode("preview")
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // Handle back to edit - use blocker for navigation
  const handleBackToEdit = () => {
    if (viewMode === "preview") {
      setViewMode("edit")
    }
  }

  // Handle save draft / create ritual
  const handleSaveDraft = async (isDraft: boolean = true) => {
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Parse duration to get minutes (e.g., "30 min" -> 30)
      const durationMatch = watch("duration")?.match(/(\d+)/)
      const durationMinutes = durationMatch ? parseInt(durationMatch[1], 10) : 30

      // Existing media from duplicate pre-fill (has uploadId). Includes
      // images and audio rows that were carried over so the duplicate
      // ritual reuses them without re-uploading. Audio entries are always
      // isCover: false — only images are eligible for cover.
      const existingImageMedia = mediaFiles.images
        .filter((img): img is typeof img & { uploadId: string } => !!img.uploadId)
        .map(img => ({ uploadId: img.uploadId, isCover: img.isCover }))
      const existingAudioMedia = (mediaFiles.audios ?? [])
        .filter((a): a is typeof a & { uploadId: string } => !!a.uploadId)
        .map(a => ({ uploadId: a.uploadId, isCover: false }))
      const existingMedia = [...existingImageMedia, ...existingAudioMedia]

      // New uploads from MediaUploadSection (have file but no uploadId)
      const imageFiles = mediaFiles.images
        .filter(img => !img.uploadId && img.file)
        .map(img => img.file)
        .filter((file): file is File => file !== undefined)

      // Cover index from combined list (existing + new uploads)
      const newUploads = mediaFiles.images.filter(img => !img.uploadId)
      const allImagesForCover = [...mediaFiles.images.filter(img => !!img.uploadId), ...newUploads]
      const coverImageIndex = allImagesForCover.findIndex(img => img.isCover)
      const validCoverIndex = coverImageIndex >= 0 ? coverImageIndex : 0

      // Get video files (filter out undefined - shouldn't happen in create flow)
      const videoFiles = mediaFiles.videos
        .map(v => v.file)
        .filter((file): file is File => file !== undefined)

      // Audio files (only newly added — those without an uploadId yet)
      const newAudios = (mediaFiles.audios ?? []).filter(a => !a.uploadId && a.file)
      const audioFiles = newAudios
        .map(a => a.file)
        .filter((file): file is File => file !== undefined)

      // Build audioPosters[] + audioMeta[] aligned with audioFiles. Each new
      // audio either has a posterFile (push to audioPosters and record its
      // index) or has no poster (posterIndex: null).
      const audioPosters: File[] = []
      const audioMeta = newAudios.map(a => {
        if (a.posterFile) {
          const posterIndex = audioPosters.length
          audioPosters.push(a.posterFile)
          return { posterIndex }
        }
        return { posterIndex: null }
      })

      // Map organizers to API format (only for non-personal rituals)
      const apiOrganizers =
        watch("visibility") === RitualVisibilityEnum.PERSONAL
          ? undefined
          : organizers.map(org => ({
              type: org.type as "user" | "community",
              id: org.id,
              inviteNote: org.inviteNote,
            }))

      // Convert privacy settings to API format (only for non-personal rituals)
      // Note: privacySettings is from useState at line 181
      const apiPrivacySettings =
        watch("visibility") === RitualVisibilityEnum.PERSONAL
          ? {
              whoCanSeeYourRitual: ["only_me"],
              whoCanComment: ["owner"],
              whoCanEditRitual: ["owner"],
              whoCanJoin: ["invite_req"],
              whoCanInvite: ["owner"],
              whoCanSeeParticipantList: ["owner"],
            }
          : {
              whoCanSeeYourRitual: watch("visibility")
                ? [watch("visibility").toLowerCase()]
                : ["everyone"],
              whoCanComment: privacySettings.comment,
              whoCanEditRitual: privacySettings.edit,
              whoCanJoin: privacySettings.join,
              whoCanInvite: privacySettings.invite,
              whoCanSeeParticipantList: privacySettings.seeParticipantList,
            }

      // Get meeting link and steps using preserved values
      const getMeetingLink = () => {
        const formValue = watch("meetingLink")
        const preservedValue = preservedValues.meetingLink
        if (!formValue && preservedValue) return preservedValue || undefined
        return formValue || undefined
      }

      const getSteps = () => {
        const formValue = watch("steps")
        const preservedValue = preservedValues.steps
        if (!formValue && preservedValue) return preservedValue || undefined
        return formValue || undefined
      }

      // Check if this is a Personal ritual
      const isPersonal = watch("visibility") === RitualVisibilityEnum.PERSONAL

      // Prepare API request for draft
      const apiRequest: CreateRitualRequest = {
        participationFee: isPersonal
          ? "Free"
          : (watch("participationFee") as "Free" | "Paid" | "Donation-based"),
        title: watch("title") || "Untitled Ritual",
        type: watch("type") as RitualTypeEnum,
        date: new Date(watch("dateTime") || new Date().toISOString()).toISOString(),
        repeat: watch("repeat") as "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom",
        repeatAt: watch("repeat") === "custom" ? watch("repeatAt") : undefined,
        repeatAtType: watch("repeat") === "custom" ? watch("repeatAtType") : undefined,
        duration: durationMinutes,
        city: watch("city") || undefined,
        state: watch("state") || undefined,
        country: watch("country") || undefined,
        lat: watch("lat"),
        long: watch("long"),
        meetingLink: getMeetingLink(),
        steps: getSteps(),
        description: watch("description") || undefined,
        intention: watch("intention") || undefined,
        accessType: watch("visibility") as RitualVisibilityEnum,
        communityId: communityIdFromUrl || undefined,
        tags: tags.map(t => ({ id: t.id, text: t.text })),
        existingMedia: existingMedia.length > 0 ? existingMedia : undefined,
        courseModules:
          watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE
            ? courseModules.map(m => ({
                title: m.title,
                description: m.description,
                duration: m.duration,
                order: m.order,
                isFree: m.isFree,
                uploadId: m.uploadId,
                videoFile: m.videoFile,
              }))
            : undefined,
        privacySettings: apiPrivacySettings,
        files: [...imageFiles, ...videoFiles],
        audioFiles,
        audioPosters,
        audioMeta,
        coverImageIndex: validCoverIndex,
        videos:
          videoUrls.length > 0
            ? videoUrls
                .filter(v => v.url && v.isProcessed && !v.isInvalid)
                .map(v => ({
                  video_url: v.url,
                  thumbnail_url: v.thumbnail || undefined,
                  title: v.title || undefined,
                }))
            : undefined,
        isDraft: isDraft,
        // Minimum participant requirement fields - excluded for Personal rituals
        minParticipantRequirement: isPersonal ? undefined : watch("minParticipantRequirement"),
        minParticipants:
          isPersonal || !watch("minParticipantRequirement")
            ? undefined
            : watch("minParticipants")
              ? parseInt(watch("minParticipants") || "0", 10)
              : undefined,
        registrationDeadline:
          isPersonal || !watch("minParticipantRequirement")
            ? undefined
            : watch("registrationDeadline") || undefined,
      }

      // Only add public-facing fields for non-Personal rituals
      if (!isPersonal) {
        apiRequest.streetAddress = watch("address") || undefined
        apiRequest.price = watch("price") ? parseFloat(watch("price") as string) : undefined
        apiRequest.feeDetails = watch("feeDetails") || undefined
        apiRequest.whatToBring = watch("whatToBring") || undefined
        apiRequest.experienceDetails = watch("whatYoullExperience") || undefined
        apiRequest.webOrSocialMediaLink = watch("websiteOrSocialMedia") || undefined
        apiRequest.organizers = apiOrganizers
      }

      // Format website URL to include https:// if missing (only for non-Personal rituals)
      if (
        !isPersonal &&
        apiRequest.webOrSocialMediaLink &&
        !apiRequest.webOrSocialMediaLink.match(/^https?:\/\//i)
      ) {
        apiRequest.webOrSocialMediaLink = `https://${apiRequest.webOrSocialMediaLink}`
      }

      // Call mutation
      const result = await _createRitualMutation.mutateAsync(apiRequest)

      // Mark AI image as used if one was selected
      if (
        mediaFiles.aiGenerationId &&
        mediaFiles.aiGenerationImageIndex !== null &&
        mediaFiles.aiGenerationImageIndex !== undefined
      ) {
        markImageUsed(mediaFiles.aiGenerationId, mediaFiles.aiGenerationImageIndex).catch(() => {})
      }

      // Set the created ritual ID and show success modal only for published rituals
      if (isDraft) {
        window.location.href = `/rituals/${result.id}`
      } else {
        setCreatedRitualId(result.id)
        setShowSuccessModal(true)
      }
    } catch (err) {
      console.error("Failed to save draft:", err)
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
        (err instanceof Error ? err.message : "Failed to save draft")
      toast.error(errorMessage)
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle leave anyway - proceed with navigation
  const handleLeaveAnyway = () => {
    blocker.proceed?.()
  }

  // Handle keep editing - stay on current page
  const handleKeepEditing = () => {
    blocker.reset?.()
  }

  // beforeunload event to warn user on browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (viewMode === "preview" && !showSuccessModal) {
        e.preventDefault()
        e.returnValue = ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [viewMode, showSuccessModal])

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return ""
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    } catch {
      return dateString
    }
  }

  // Helper function to format time
  const formatTime = (dateTimeString: string) => {
    if (!dateTimeString) return ""
    try {
      const date = new Date(dateTimeString)
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    } catch {
      return ""
    }
  }

  // Track blob URLs for cleanup on unmount only
  const blobUrlsRef = useRef<string[]>([])

  // Get cover image URL - create and cache in ref for cleanup
  const getCoverImageUrl = () => {
    if (!mediaFiles.images || mediaFiles.images.length === 0) {
      return getAssetUrl("image.png")
    }
    const coverImage = mediaFiles.images.find(img => img.isCover)
    const targetFile = coverImage?.file || mediaFiles.images[0].file
    const targetSrc = coverImage?.src || mediaFiles.images[0].src
    if (!targetFile && !targetSrc) {
      return getAssetUrl("image.png")
    }
    if (targetFile) {
      const url = URL.createObjectURL(targetFile)
      blobUrlsRef.current.push(url)
      return url
    }
    return targetSrc
  }

  // Get all image URLs - create and cache in ref for cleanup
  const getImageUrls = () => {
    if (!mediaFiles.images || mediaFiles.images.length === 0) {
      return []
    }
    const urls = mediaFiles.images
      .map(img => {
        if (img.file) {
          const url = URL.createObjectURL(img.file)
          blobUrlsRef.current.push(url)
          return url
        }
        return img.src || null
      })
      .filter((url): url is string => url !== null)
    return urls
  }

  // Cleanup blob URLs ONLY on component unmount (empty deps array)
  useEffect(() => {
    return () => {
      // Revoke all blob URLs created during the component's lifetime
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  // Contextual tips for each section - defined before validateSectionTips
  const getSectionTips = useCallback((sectionId: string | null) => {
    const tipsMap: Record<string, { title: string; tips: string[]; showPreview?: boolean }> = {
      info: {
        title: "Info",
        tips: [
          "Use a clear, descriptive title",
          "Avoid special symbols or emojis in the title",
          "Choose the correct ritual type",
          "Set an accurate date and duration",
          "Add a full address for in-person rituals",
          "Use repeat if the ritual happens regularly",
        ],
      },
      details: {
        title: "Details",
        tips: [
          "Describe the ritual clearly and step by step",
          "Mention any items participants should bring",
          "Share what participants will experience",
          "Explain the intention or purpose of the ritual",
          "Avoid links or promotion in the description",
          "Use relevant tags to help others discover it",
        ],
      },
      organizers: {
        title: "Organizer",
        tips: [
          "Add co-organizers if the ritual is led by multiple people or communities",
          "Invite ritualists who actively guide the experience",
          "Ensure each organizer has a complete profile",
          "Only add organizers who are involved",
          "You can manage or remove organizers later",
        ],
      },
      media: {
        title: "Upload Media",
        tips: [
          "Upload a clear cover image that reflects the ritual atmosphere",
          "Use natural lighting and calm, authentic visuals",
          "Avoid text or logos on images",
          "Keep images high quality and relevant to the ritual",
          "Add a video if it helps explain the experience",
          "Keep media high quality and relevant to the ritual",
        ],
        showPreview: true,
      },
      privacy: {
        title: "Privacy",
        tips: [
          "Choose who can see your ritual before publishing",
          "Limit comments if you want a quieter space",
          "Allow edits only for trusted organizers",
          "Use request to join for smaller or private groups",
          "Review privacy settings before sharing the ritual",
        ],
      },
      courseModules: {
        title: "Course Modules",
        tips: [
          "Add at least one module with title, description, and video",
          "Organize modules in a logical sequence",
          "Keep video content focused and relevant",
          "Set appropriate duration for each module",
          "Preview module content before publishing",
        ],
      },
    }

    return tipsMap[sectionId || "info"] || tipsMap.info
  }, [])

  // Map organizers to preview format
  const mapOrganizers = (): PreviewOrganizer[] => {
    return organizers.map(org => ({
      id: org.id,
      name: org.name,
      role: org.roleName,
      avatar: org.avatar,
      socialLinks: [],
      type: org.type,
      followers: org.type === "community" ? org.dataCount?.members : org.dataCount?.followers,
    }))
  }

  // Handle accordion item close - trigger validation
  const handleItemClose = (closedItemId: string) => {
    if (closedItemId === "courseModules") {
      // Defer validation to avoid updating state during render
      setTimeout(() => {
        validateCourseModules()
      }, 0)
    }
  }

  return (
    <>
      {isLoadingSource && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-brand-olive border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading ritual...</p>
          </div>
        </div>
      )}
      {viewMode === "edit" ? (
        <div className="min-h-screen bg-background max-w-360 mx-auto px-5 md:px-17.5">
          <div className="max-w-8xl mx-auto px-5 sm:px-15 py-10">
            {/* Main Content - 2 Column Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Accordions (70%) */}
              <div className="lg:col-span-2 max-w-[850px]" ref={accordionContainerRef}>
                <Accordion
                  ref={accordionRef}
                  items={accordionItems}
                  defaultOpenIndex={0}
                  allowMultiple={false}
                  onNextClick={handleNext}
                  onItemClose={handleItemClose}
                  onOpenChange={handleAccordionOpenChange}
                  onSectionAttempt={sectionId => {
                    setAttemptedSections(prev => new Set([...prev, sectionId]))
                  }}
                  accordionItemRef={setAccordionRef}
                  attemptedSections={attemptedSections}
                />
              </div>

              {/* Right Column - Sidebar (30%) */}
              <div className="sticky md:top-4">
                {/* Spacer div to align tips with active accordion - Uses dynamic offset */}
                <div
                  style={{
                    height: `${sidebarOffset}px`,
                    transition: "height 250ms ease-in-out",
                  }}
                />

                {/* Contextual Tips Card - Smooth transition for position changes */}
                {/* Media Preview Card - shown only for Upload Media section */}
                {openAccordionId === "media" &&
                  getSectionTips("media").showPreview &&
                  mediaFiles.images.length > 0 && (
                    <div className="my-5">
                      <p className="text-[18px] font-semibold leading-6.25 text-body mb-3.75">
                        Card Preview
                      </p>
                      <div className="grid grid-cols-2 gap-2.5">
                        {mediaFiles.images.map((img, index) => {
                          if (!img.file) return null
                          const previewUrl = URL.createObjectURL(img.file)
                          // Store URL for cleanup
                          blobUrlsRef.current.push(previewUrl)
                          return (
                            <div
                              key={img.id}
                              className="relative aspect-video rounded-tl-lg rounded-br-lg overflow-hidden bg-muted"
                            >
                              <img
                                src={previewUrl}
                                alt={`Image ${index + 1} preview`}
                                className="w-full h-full object-cover"
                              />
                              {img.isCover && (
                                <div className="absolute top-1 left-1 bg-brand-olive text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                                  Cover
                                </div>
                              )}
                              <div className="absolute bottom-0 left-0 p-2 space-y-1">
                                <div className="flex items-center gap-1">
                                  <div className="h-1.5 w-12 bg-white/60"></div>
                                  <div className="h-1.5 w-5 bg-white/60"></div>
                                  <div className="h-1.5 w-12 bg-white/60"></div>
                                  <div className="h-1.5 w-13 bg-white/60"></div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="h-1 w-12 bg-white/60"></div>
                                  <div className="h-1 w-5 bg-white/60"></div>
                                  <div className="h-1 w-5 bg-white/60"></div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                <div
                  className={cn("rounded-[20px] border p-5 transition-colors bg-card border-card")}
                  style={{
                    transition:
                      "background-color 250ms ease-in-out, border-color 250ms ease-in-out",
                  }}
                >
                  <div>
                    <h3 className="text-[18px] leading-6.25 font-semibold text-primary mb-3.75">
                      {getSectionTips(openAccordionId).title} Tips
                    </h3>
                  </div>
                  <ul className="space-y-2 text-sm list-disc list-inside">
                    {getSectionTips(openAccordionId).tips.map((tip, index) => (
                      <li key={index} className="text-sm leading-6.25 text-primary">
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Card */}
                <div className="mt-6 border bg-card border-card rounded-lg p-5">
                  {/* Error Message */}
                  {submitError && (
                    <div className="p-3 rounded-md">
                      <p className="text-sm text-brand-terracotta-bright">{submitError}</p>
                    </div>
                  )}

                  {/* Preview Button */}
                  <Button
                    onClick={handlePreview}
                    disabled={accordionItems.some(item => item.error)}
                    className="w-full uppercase text-sm mb-3.75"
                    variant="secondary"
                  >
                    {t("createRitual.buttons.previewRitual")}
                  </Button>

                  {/* Save Draft Button */}
                  <Button
                    onClick={() => handleSaveDraft()}
                    className="w-full uppercase text-sm"
                    variant="outline"
                    disabled={isSubmitting}
                  >
                    {t("createRitual.buttons.saveDraft")}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Organizer Selection Modal */}
          {isOrganizerModalOpen && (
            <SelectRitualOrganizersModal
              isOpen={isOrganizerModalOpen}
              onClose={() => setIsOrganizerModalOpen(false)}
              onAddOrganizers={handleAddOrganizers}
              existingOrganizerIds={organizers.map(org => org.id)}
              currentUserId={user?.id}
            />
          )}

          {/* Profile Incomplete Modal */}
          <ProfileIncompleteModal
            isOpen={showProfileIncompleteModal}
            user={user}
            closeOnly={true}
            onClose={() => setShowProfileIncompleteModal(false)}
          />
        </div>
      ) : (
        // Preview Mode
        <PreviewRitual
          title={watch("title") || t("createRitual.fallback.untitledRitual")}
          description={watch("description") || ""}
          type={
            (ritualTypeOptions.find(opt => opt.value === watch("type"))?.label ||
              t("createRitual.fallback.inPerson")) as RitualTypeLabel
          }
          fee={
            watch("participationFee") === "Paid" && watch("price")
              ? `$${watch("price")}`
              : watch("participationFee") || t("createRitual.options.participationFee.free")
          }
          duration={watch("duration") || ""}
          date={formatDate(watch("dateTime") || "")}
          time={formatTime(watch("dateTime") || "")}
          repeat={
            repeatOptions.find(opt => opt.value === watch("repeat"))?.label ||
            t("repeat.options.none")
          }
          location={watch("address") || ""}
          meetingLink={watch("meetingLink") || undefined}
          websiteLink={watch("websiteOrSocialMedia") || undefined}
          whatYoullNeed={
            watch("whatToBring")
              ?.split("\n")
              .filter(item => item.trim()) || []
          }
          whatYoullExperience={
            watch("whatYoullExperience")
              ?.split("\n")
              .filter(item => item.trim()) || []
          }
          intention={watch("intention") || ""}
          steps={
            watch("steps")
              ?.split("\n")
              .filter(item => item.trim()) || []
          }
          organizers={mapOrganizers()}
          coverImage={getCoverImageUrl() || ""}
          images={getImageUrls()}
          tags={tags.map(t => t.text)}
          courseModules={
            watch("type") === "SELF_GUIDED_COURSE"
              ? courseModules.map(m => ({
                  id: m.id,
                  title: m.title,
                  description: m.description,
                  duration: m.duration,
                  order: m.order,
                  isFree: m.isFree,
                  videoUrl: m.videoPreview,
                }))
              : undefined
          }
          onBackToEdit={handleBackToEdit}
          onCreateRitual={() => handleSaveDraft(false)}
          onSaveDraft={() => handleSaveDraft()}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Leave Confirmation Modal - rendered outside viewMode conditional so it shows in both modes */}
      <LeaveConfirmationModal
        isOpen={showLeaveConfirmationModal}
        onKeepEditing={handleKeepEditing}
        onLeave={handleLeaveAnyway}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onViewRitual={() => {
          setShowSuccessModal(false)
          if (createdRitualId) {
            window.location.href = `/rituals/${createdRitualId}`
          }
        }}
        onBackToRituality={() => {
          setShowSuccessModal(false)
          window.location.href = "/"
        }}
        ritualId={createdRitualId || undefined}
      />
    </>
  )
}
